import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import {
  wixCodeCheckOutBuyerChoosesCountry,
  wixCodeCheckOutBuyerMovesInAFunnel,
  wixCodeCheckOutBuyerViewsOrderDetails,
  wixCodeCheckOutErrorOccurred,
  wixCodeCheckOutPageLoaded,
  wixCodeCheckOutPageStartedLoading,
  uouPayButtonPageOpen,
  uouPayButtonPageSubmited,
  uouPayButtonPaymentStarted,
  uouPayButtonResultPage,
} from '@wix/bi-logger-payments-data/v2';
import { BuyerServerTransactionStatus } from '@wix/cashier-common/dist/src/enums/transactions/BuyerServerTransactionStatus';
import { AppInstanceResolver } from '@wix/cashier-common/dist/src/utils/AppInstanceResolver';
import { OrderService } from '@wix/cashier-core/dist/src/services/Order';
import { type IOrder, type IOrderUpdated } from '@wix/cashier-core/dist/src/services/Order/types';
import {
  type PaymentsWidgetConfiguration,
  type MandatoryFieldsMap,
  type PaymentCompleteFailResult,
  type PaymentCompleteResult,
  type PaymentsWidgetAPI,
} from '@wix/cashier-payments-widget';
import { PaymentsWidget } from '@wix/cashier-payments-widget/lazy';
import cn from 'clsx';
import { alpha3ToAlpha2 } from 'i18n-iso-countries';
import * as PropTypes from 'prop-types';
import React, { Component } from 'react';
import { isRTLLocale } from '@wix/cashier-common/dist/src/utils/isRTLLocale';
import { LockLocked, ChevronLeftSmall, Dismiss } from '@wix/wix-ui-icons-common';
import { type AppInstance } from '@wix/cashier-common/dist/src/types/AppInstance';
import { AppId } from '@wix/cashier-common/dist/src/enums/AppId';
import { type CountryAlpha3 } from '@wix/cashier-common/dist/src/utils/countryCodes';
import { type UserInfo } from '../../types/UserInfo';
import { pickDefined } from '../../utils/pickDefined';
import { OrderSummaryPopover } from '../OrderSummaryPopover';
import { PaymentComplete } from '../PaymentComplete/PaymentComplete';
import ProgressBar from '../ProgressBar/ProgressBar';
import { DeviceTypeEnum } from '../../types/DeviceType';
import { InteractionName } from '../../types/InteractionName';
import { type IPayAppComponentProps } from '../../types/PayApp';
import { PaymentProcessStep, PaymentProcessStepToBi } from '../../types/PaymentProcessStep';
import { Theme } from '../../types/Theme';
import { biLogger, updateBiLoggerDefaults } from '../../utils/biLogger';
import { biLoggerPayButton, updatePayButtonBiLoggerDefaults } from '../../utils/biLoggerPayButton';
import { postMessage } from '../../utils/communicationUtils';
import { isCancelledStatus, isFailedStatus } from '../../utils/serverStatusToClient';
import { trackError } from '../../utils/errorReporter';
import ContinueButton from '../ContinueButton';
import { GeneralError } from '../GeneralError';
import Loader from '../Loader/Loader';
import NoPaymentMethodContainer from '../NoPaymentMethods/NoPaymentMethodContainer';
import { UserInfoForm } from '../UserInfoForm';
import { TabIndex } from '../../utils/tabIndexes';
import { DH } from '../../utils/DataHook';
import { t } from '../../utils/i18n';
import { filterDefined } from '../../utils/filterDefined';
import { Panorama } from '../../utils/panorama';
import styles from './PayAppComponent.scss';

interface IContext {
  deviceType: string;
}

interface ICashierPayAppComponentState {
  generalErrorMessage: string;
  order?: IOrder;
  currentStep: PaymentProcessStep;
  paymentMethods: PaymentMethod[];
  paymentResult?: PaymentCompleteResult;
  userInfoFormData: UserInfo;
  contentGradient: boolean;
  paymentWidgetIsValid: boolean;
  userInfoFormIsValid: boolean;
  userInfoFormDisabled: boolean;
  initialLoading: boolean;
  isProcessing: boolean;
}
interface InjectedProps {
  isLoading: boolean;

  startModuleLoad(moduleName: string): void;
  endModuleLoad(moduleName: string): void;
}

export class PayAppComponent extends Component<IPayAppComponentProps & InjectedProps, ICashierPayAppComponentState> {
  private paymentProcessStepsOrder: PaymentProcessStep[];
  private userInfoForm: UserInfoForm;
  private paymentWidget: PaymentsWidgetAPI;
  private contentNode;
  private contentScrollableNode;
  private autoFocusNode;
  private rootNode;
  private rootNodeHeight = 0;
  private startLoadingTimestamp = 0;
  private biLoggerTimestamp = 0;
  private appInstanceResolver = new AppInstanceResolver();

  getChildContext(): IContext {
    const { deviceType } = this.props;
    return { deviceType };
  }

  static defaultProps = {
    allowManualPayment: true,
  };

  static childContextTypes = {
    deviceType: PropTypes.string,
  };

  constructor(props, context) {
    super(props, context);

    this.paymentProcessStepsOrder = [PaymentProcessStep.DETAILS, PaymentProcessStep.COMPLETE];

    this.state = {
      generalErrorMessage: '',
      order: null,
      paymentMethods: undefined,
      paymentResult: null,
      currentStep: null,
      userInfoFormData: props.userInfo,
      contentGradient: false,
      paymentWidgetIsValid: false,
      userInfoFormIsValid: false,
      userInfoFormDisabled: false,
      initialLoading: true,
      isProcessing: false,
    };

    this.biLoggerTimestamp = this.props.biLoggerTimestamp;
  }
  UNSAFE_componentWillMount() {
    updateBiLoggerDefaults({
      appId: this.props.appId,
      appInstanceId: this.props.appInstanceId,
      orderSnapshotId: this.props.snapshotId,
      msid: this.props.msid,
      visitorId: this.props.visitorId,
      termsAndConditions: Boolean(this.props.termsAndConditionsLink),
      showThankYouPage: Boolean(this.props.showThankYouPage),
      merchantDefinedFields: Object.keys(this.props.userInfo || {})
        .filter((key) => Boolean(this.props.userInfo[key]))
        .join(','),
    });

    updatePayButtonBiLoggerDefaults(
      pickDefined({
        appid: this.props.appId,
        appInstanceId: this.props.appInstanceId,
        memberId: this.props.siteMemberId,
        msid: this.props.msid,
        origin: this.props.pbOrigin,
        paybuttonid: this.props.pbId,
        sessionId: this.props.sessionId,
        visitorId: this.props.visitorId,
      }),
    );

    this.logPageLoadingBIEvent(this.state.currentStep);
    this.fetchOrder();
    this.props.startModuleLoad('state:paymentMethods');
    this.unsubscribeOnKeyDown();
  }

  componentDidMount() {
    this.scrollToTop();
    this.props.endModuleLoad('CashierPayApp');
    this.reportLoaderShown();
    this.subscribeOnKeyDown();
    this.resetFocus();

    Panorama.client?.transaction(InteractionName.PAYMENTS_WIDGET_LOAD).start();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.initialLoading && !this.props.isLoading && prevProps.isLoading) {
      this.setState({ initialLoading: false }, () => this.reportAppLoaded());
    }
    if (this.state.generalErrorMessage && this.state.generalErrorMessage !== prevState.generalErrorMessage) {
      this.scrollToTop();
    }
    if (this.state.currentStep !== prevState.currentStep) {
      this.resetFocus();
      this.setState({ isProcessing: false });

      if (this.state.currentStep && PaymentProcessStepToBi[this.state.currentStep]) {
        biLoggerPayButton.report(
          uouPayButtonPageOpen({
            pagename: PaymentProcessStepToBi[this.state.currentStep],
          }),
        );
      }
    }
    this.updateContentGradient();
    this.logPageLoadedBIEvent(prevProps, prevState);
    this.sendSizeNotification();
  }

  componentWillUnmount() {
    this.appInstanceResolver.destroy();
  }

  private subscribeOnKeyDown = () => {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleKeyDown);
    }
  };

  private unsubscribeOnKeyDown = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeyDown);
    }
  };

  private reportLoaderShown = () => {
    Panorama.client?.transaction(InteractionName.ALE_LOADER).finish();
    Panorama.client?.transaction(InteractionName.ALE_RENDER).start();
  };

  private reportAppLoaded = () => {
    Panorama.client?.reportLoadFinish();
    Panorama.client?.transaction(InteractionName.ALE_RENDER).finish();
  };

  get configuration(): PaymentsWidgetConfiguration {
    return {
      appId: AppId.Cashier,
      appInstanceId: this.props.appInstanceId,
      snapshotId: this.props.snapshotId,
      locale: this.props.locale,
      visitorId: this.props.visitorId,
      msid: this.props.msid,
      amount: this.state.order ? String(this.state.order.description.amount) : undefined,
      currency: this.state.order?.description.currency,
      ...(this.state.userInfoFormData && {
        country: this.state.userInfoFormData.country as CountryAlpha3,
      }),
      appInstance: this.getAppInstance(),
    };
  }

  get currentStepIndex(): number {
    return this.paymentProcessStepsOrder.indexOf(this.state.currentStep);
  }

  private getAppInstance = (): AppInstance => this.appInstanceResolver.appInstance;

  private async fetchOrder() {
    this.props.startModuleLoad('state:order');
    let order: IOrder;

    try {
      order = await new OrderService({
        baseURL: this.props.host,
        appInstance: this.getAppInstance(),
        revalidateAppInstance: this.appInstanceResolver.revalidate,
      }).fetchOrder(this.props.snapshotId);
    } catch (error) {
      trackError(error, {
        messagePrefix: 'PayAppComponent: Failed to fetch order',
      });

      this.setState({
        generalErrorMessage: this.getGeneralErrorMessage('order'),
      });
    }

    if (order) {
      this.setState({ order });

      if (!this.props.forceSkipUserInfoPage) {
        const { billingAddress } = order.description;

        const isBillingAddressFilled =
          billingAddress &&
          billingAddress.firstName &&
          billingAddress.lastName &&
          billingAddress.email &&
          billingAddress.countryCode;

        if (!this.props.skipUserInfoPage || !isBillingAddressFilled) {
          this.paymentProcessStepsOrder = [PaymentProcessStep.USER_INFO, ...this.paymentProcessStepsOrder];
        }
      }
    }

    const currentStep = this.paymentProcessStepsOrder[0];

    if (currentStep === this.getFirstPaymentWidgetStep()) {
      this.props.startModuleLoad('CashierPaymentsWidget');
    }

    this.props.endModuleLoad('state:order');

    this.setState({ currentStep });
  }

  getActivePM = () => this.paymentWidget?.getActivePaymentMethod();
  getActivePmId = () => this.getActivePM()?.methodId;

  private getBIEventDefaults = () => {
    return {
      ...(this.state.currentStep && {
        page: this.state.currentStep,
      }),
      selectedCountry: (this.state.userInfoFormData && alpha3ToAlpha2(this.state.userInfoFormData.country)) || '',
      paymentMethodsAvailable: this.state.paymentMethods?.join(','),
      paymentMethodSelected: this.state.currentStep === PaymentProcessStep.USER_INFO ? '' : this.getActivePmId(),
    };
  };

  private logPageLoadedBIEvent = (prevProps, prevState) => {
    const firstStepLoaded = this.currentStepIndex === 0 && !this.props.isLoading && prevProps.isLoading;
    const shouldSend = prevState.currentStep !== this.state.currentStep || firstStepLoaded;
    if (!shouldSend || !this.startLoadingTimestamp) {
      return;
    }

    const timestamp = Date.now();
    biLogger.report(
      wixCodeCheckOutPageLoaded({
        ...this.getBIEventDefaults(),
        duration: timestamp - this.startLoadingTimestamp,
        fullDuration: this.biLoggerTimestamp ? timestamp - this.biLoggerTimestamp : undefined,
        ...(prevState.currentStep && {
          pageFrom: prevState.currentStep,
        }),
        status: this.state.paymentResult?.status || '',
      }),
    );
    this.startLoadingTimestamp = 0;
    this.biLoggerTimestamp = 0; // only for first page load
  };

  private logPageLoadingBIEvent = (step) => {
    this.startLoadingTimestamp = this.startLoadingTimestamp || Date.now();
    biLogger.report(
      wixCodeCheckOutPageStartedLoading({
        ...this.getBIEventDefaults(),
        ...(step && {
          page: step,
        }),
        ...(this.state.currentStep && {
          pageFrom: this.state.currentStep,
        }),
        status: this.state.paymentResult?.status || '',
        duration: this.biLoggerTimestamp ? this.startLoadingTimestamp - this.biLoggerTimestamp : undefined,
      }),
    );
  };

  private logBuyerMovesInAFunnelBIEvent = (direction, pageTo = '') => {
    biLogger.report(
      wixCodeCheckOutBuyerMovesInAFunnel({
        ...this.getBIEventDefaults(),
        direction,
        pageTo,
      }),
    );
  };

  private logBuyerViewsOrderDetailsBIEvent = () => {
    biLogger.report(wixCodeCheckOutBuyerViewsOrderDetails(this.getBIEventDefaults()));
  };

  private logBuyerChoosesCountryBIEvent = (country: string) => {
    biLogger.report(
      wixCodeCheckOutBuyerChoosesCountry({
        ...this.getBIEventDefaults(),
        selectedCountry: country,
      }),
    );
  };

  private logErrorOccurredBIEvent = (errorType, errorDesc) => {
    biLogger.report(
      wixCodeCheckOutErrorOccurred({
        ...this.getBIEventDefaults(),
        errorDesc,
        errorType,
      }),
    );
  };

  private resetFocus = () => {
    this.autoFocusNode?.focus();
  };

  private getGeneralErrorMessage = (errorType: string) => {
    let text;
    let BIText;
    switch (errorType) {
      case 'userInfoForm':
        text = t('payments.app.userInfo.generalError');
        BIText = `User info: can't save`;
        break;
      case 'order':
        text = t('payments.app.generalError');
        BIText = `Order: can't load`;
        break;
      case 'paymentMethod':
        text = t('payments.app.generalError');
        BIText = `Payment Methods: can't load`;
        break;
      default:
        return '';
    }

    this.logErrorOccurredBIEvent('generic', BIText);
    return text;
  };

  private getFirstPaymentWidgetStep = () => {
    return PaymentProcessStep.DETAILS;
  };

  private saveUserInfoAndGoToNextStep = () => {
    const { isValid, invalidFields } = this.userInfoForm.componentAPI.validate();
    if (!isValid) {
      this.logErrorOccurredBIEvent('input', invalidFields.join(','));
      return this.userInfoForm.componentAPI.markInvalidFields();
    }

    if (this.state.userInfoFormDisabled) {
      return this.goToNextStep();
    }

    this.setState({ isProcessing: true });
    this.props.startModuleLoad('userInfoForm:save');

    this.userInfoForm.componentAPI
      .save({ skipContactCreation: this.props.skipContactCreation })
      .then((order: IOrderUpdated) => {
        this.setState({
          order: {
            ...this.state.order,
            description: order.description,
          },
        });

        this.props.endModuleLoad('userInfoForm:save');
        this.goToNextStep();
      })
      .catch((error) => {
        trackError(error, {
          messagePrefix: 'PayAppComponent: Failed to save user info',
        });

        this.setState({
          generalErrorMessage: this.getGeneralErrorMessage('userInfoForm'),
          isProcessing: false,
        });

        this.props.endModuleLoad('userInfoForm:save');
      });
  };

  private getUserInfoFields = (): MandatoryFieldsMap => {
    const { userInfoFormData: { country, ...otherUserInfo } = {} } = this.state;

    return filterDefined({
      ...otherUserInfo,
      countryCode: country,
    });
  };

  private startPay = async () => {
    this.setState({ isProcessing: true });

    this.paymentWidget?.validate().then(({ isValid, invalidFields }) => {
      // validate, but not block click flow(bug with blocked popup)
      if (!isValid) {
        this.logErrorOccurredBIEvent('input', invalidFields.join(','));
      }
    });

    if (this.paymentWidget && this.state.paymentWidgetIsValid) {
      this.setState({
        paymentResult: null,
        userInfoFormDisabled: true,
      });
      this.props.startModuleLoad('PaymentStart');

      try {
        const activePM = this.getActivePM();
        // TODO: cover with test
        biLoggerPayButton.report(
          uouPayButtonPaymentStarted({
            orderSnapshotId: this.state.order.id,
            pagename: PaymentProcessStepToBi[this.state.currentStep],
            paymentMethodType: 'online',
            paymentCategory: activePM.methodId,
            paymentProvider: activePM.connectedProviderId,
          }),
        );
      } catch {
        /* ignored */
      }

      try {
        await this.paymentWidget.pay();
      } catch (error) {
        trackError(error, {
          messagePrefix: 'PayApp: failed to pay',
        });
        // TODO: handle error
        // const validateError: Awaited<ReturnType<PaymentsWidgetAPI['validate']>> = error;
        //
        // if (validateError.isValid === false) {
        //   this.setState({
        //     isProcessing: false,
        //     userInfoFormDisabled: false,
        //     generalErrorMessage: this.getGeneralErrorMessage('paymentMethod'),
        //   });
        // }
      }
    }
  };

  private goToStepAfterPayment = (paymentResult: PaymentCompleteResult, isCreditCard = false) => {
    const isSuccess = !isFailedStatus(paymentResult.serverStatus);

    if (isSuccess) {
      biLoggerPayButton.report(
        uouPayButtonResultPage({
          errorMessage: '',
          result: true,
        }),
      );
      return PaymentProcessStep.COMPLETE;
    }

    biLoggerPayButton.report(
      uouPayButtonResultPage({
        errorMessage: (paymentResult as PaymentCompleteFailResult).translatedError,
        result: false,
      }),
    );

    if (isCreditCard) {
      return PaymentProcessStep.DETAILS;
    }

    return this.getFirstPaymentWidgetStep();
  };

  private updateContentGradient = () => {
    if (this.contentNode && this.contentScrollableNode) {
      const contentGradient =
        this.contentScrollableNode.scrollHeight > this.contentScrollableNode.offsetHeight &&
        this.contentScrollableNode.scrollHeight >
          this.contentScrollableNode.scrollTop + this.contentScrollableNode.offsetHeight;
      if (contentGradient !== this.state.contentGradient) {
        this.setState({ contentGradient });
      }
    }
  };

  scrollToTop = () => {
    window.scrollTo(0, 0);
    window.Wix.scrollTo(0, 0);
  };

  handlePaymentMethodsLoad = (paymentMethods: { paymentMethod: PaymentMethod }[] = []) => {
    this.setState({
      paymentMethods: paymentMethods.map(({ paymentMethod }) => paymentMethod as PaymentMethod),
    });

    this.props.endModuleLoad('state:paymentMethods');
  };

  handlePaymentsWidgetApiInit = (api: PaymentsWidgetAPI) => {
    this.paymentWidget = api;
  };
  setContentRef = (ref) => (this.contentNode = ref);
  setContentScrollableRef = (ref) => (this.contentScrollableNode = ref);

  handlePaymentError = (method) => {
    this.logErrorOccurredBIEvent('generic', 'Transaction failure');
    if (!this.state.paymentResult) {
      this.handlePaymentComplete(method, {
        status: BuyerServerTransactionStatus.Failed,
        serverStatus: BuyerServerTransactionStatus.Failed,
      } as any);
    }
    this.scrollToTop();
  };

  handlePaymentStart = () => {
    this.props.startModuleLoad('PaymentDetails');
  };

  handleErrorFetchSettings = () => {
    this.goToStep(this.paymentProcessStepsOrder[0], {
      isProcessing: false,
      generalErrorMessage: this.getGeneralErrorMessage('paymentMethod'),
    });
    this.props.endModuleLoad('state:paymentMethods');
  };

  handlePaymentComplete = (method, paymentResult: PaymentCompleteResult) => {
    this.setState({ isProcessing: false });
    const isCreditCard = method === PaymentMethod.CreditCard;
    postMessage({
      type: 'PAYMENT_COMPLETE',
      method,
    });

    if (isCancelledStatus(paymentResult.serverStatus)) {
      biLoggerPayButton.report(
        uouPayButtonResultPage({
          errorMessage: (paymentResult as PaymentCompleteFailResult).translatedError,
          result: false,
        }),
      );
      this.goToStep(this.getFirstPaymentWidgetStep());
      return;
    }
    const stepAfterPayment = this.goToStepAfterPayment(paymentResult, isCreditCard);
    this.goToStep(stepAfterPayment, {
      paymentResult,
    });
    this.props.endModuleLoad('PaymentStart');
  };

  paymentWidgetIsValidChanged = (status) => {
    if (status !== this.state.paymentWidgetIsValid) {
      this.setState({ paymentWidgetIsValid: status });
    }
  };

  onUserInfoFormChanged = (isValid, userInfo, isChangedProgrammatically?: boolean) => {
    const newState: Partial<ICashierPayAppComponentState> = {};

    if (isValid !== this.state.userInfoFormIsValid) {
      newState.userInfoFormIsValid = isValid;
    }
    if (isValid) {
      newState.userInfoFormData = userInfo;
    }
    if (
      isValid &&
      (!this.state.userInfoFormData || userInfo.country !== this.state.userInfoFormData.country) &&
      !isChangedProgrammatically
    ) {
      this.logBuyerChoosesCountryBIEvent(userInfo.country);
    }

    if (Object.keys(newState).length) {
      this.setState(newState as any);
    }
  };

  handleKeyDown = (event) => {
    if (event.code === 'Escape') {
      this.closeWidget();
    }
  };

  closeWidget = () => {
    this.logBuyerMovesInAFunnelBIEvent('close');
    if (typeof window !== 'undefined' && window.Wix) {
      window.Wix.closeWindow();
    } else {
      throw new Error(`PayApp is out of Wix context and can't be closed as a popup.`);
    }
  };

  sendSizeNotification = () => {
    if (!this.rootNode) {
      return;
    }
    const height = this.rootNode.offsetHeight;
    if (this.props.deviceType === DeviceTypeEnum.mobile && height !== this.rootNodeHeight) {
      this.rootNodeHeight = height;
      window.Wix.setHeight(this.rootNodeHeight);
    }
  };

  handleLoadingChanged = (key: string, status: boolean) => {
    if (status) {
      this.props.startModuleLoad(key);
    } else {
      this.props.endModuleLoad(key);
    }
  };

  handleWidgetCrash = (error?: Error | string) => {
    this.props.endModuleLoad('CashierPaymentsWidget');

    trackError(error instanceof Error ? error : new Error(error || 'Unknown error'), {
      messagePrefix: 'PayAppComponent: Payments widget crashed',
    });
  };
  goToNextStep = () => {
    const nextStep = this.paymentProcessStepsOrder[this.currentStepIndex + 1];
    this.goToStep(nextStep);
  };

  goToStep(nextStep: PaymentProcessStep, nextState?: Partial<ICashierPayAppComponentState>) {
    this.logPageLoadingBIEvent(nextStep);

    this.setState({
      ...(nextState as ICashierPayAppComponentState),
      currentStep: nextStep,
    });
  }
  renderHeader() {
    const showHeaderUI = this.state.currentStep !== PaymentProcessStep.COMPLETE;

    return (
      <div className={styles.header}>
        {showHeaderUI && (
          <div className={styles.columns}>
            {this.currentStepIndex === 0 && this.renderAppTitle()}
            {this.currentStepIndex !== 0 && this.renderBackButton()}
            {this.state.order && this.renderOrderSummary()}
          </div>
        )}
        {showHeaderUI && this.renderProgressBar()}
        <button
          data-hook="wcn-close-button"
          aria-label={t('payments.app.accessibility.closeButton')}
          className={styles.closeButton}
          onClick={this.closeWidget}
          tabIndex={TabIndex.CloseButton}
        >
          <Dismiss />
        </button>
      </div>
    );
  }

  renderFooter() {
    return (
      <div className={styles.footer}>
        {this.renderContinueButton()}
        {this.renderSSLNotice()}
      </div>
    );
  }

  renderStepTitle() {
    const title = t(`payments.app.${this.state.currentStep}.step`, {
      stepNumber: this.currentStepIndex + 1,
    });

    return (
      <div
        data-hook={DH.StepTitle}
        data-step={this.state.currentStep}
        className={styles.stepTitle}
        aria-label={this.state.currentStep && t(`payments.app.accessibility.${this.state.currentStep}.step`)}
        tabIndex={TabIndex.Usual}
      >
        {title}
      </div>
    );
  }

  renderAppTitle() {
    return (
      <span data-hook="wcn-app-title" className={styles.appTitle}>
        {t('payments.app.title')}
      </span>
    );
  }

  renderBackButton() {
    const prevStep = this.paymentProcessStepsOrder[this.currentStepIndex - 1];
    const handlerWrapper = () => {
      this.logBuyerMovesInAFunnelBIEvent('back', prevStep);
      this.goToStep(prevStep);
    };
    return (
      <button className={styles.backButton} data-hook={DH.BackButton} onClick={handlerWrapper}>
        <ChevronLeftSmall /> <span>{t('payments.app.backButton')}</span>
      </button>
    );
  }

  renderOrderSummary() {
    return (
      <OrderSummaryPopover
        order={this.state.order}
        dateNumberFormat={this.props.dateNumberFormat}
        onShown={this.logBuyerViewsOrderDetailsBIEvent}
      />
    );
  }

  renderProgressBar() {
    return (
      <div className={styles.progressBar}>
        <ProgressBar steps={this.paymentProcessStepsOrder.slice(0, -1)} currentStep={this.state.currentStep} />
      </div>
    );
  }

  renderContinueButton() {
    const nextStep = this.paymentProcessStepsOrder[this.currentStepIndex + 1];
    let label = 'payments.app.continueButton';
    let ariaLabel = 'payments.app.accessibility.continueButton';
    let handler = this.goToNextStep;
    let disabled = false;

    if (this.state.currentStep === PaymentProcessStep.COMPLETE) {
      label = 'payments.app.returnButton';
      handler = this.closeWidget;
    } else if (this.state.currentStep === PaymentProcessStep.USER_INFO) {
      handler = this.saveUserInfoAndGoToNextStep;
      disabled = !this.state.userInfoFormIsValid;
    } else if (this.state.currentStep === PaymentProcessStep.DETAILS) {
      ariaLabel = label = 'payments.app.payButton';
      handler = this.startPay;
      disabled = !this.state.paymentWidgetIsValid;
    }

    const handlerWrapper = () => {
      biLoggerPayButton.report(
        uouPayButtonPageSubmited({
          pagename: PaymentProcessStepToBi[this.state.currentStep],
        }),
      );

      this.setState(
        {
          generalErrorMessage: '',
        },
        () => {
          this.logBuyerMovesInAFunnelBIEvent('continue', nextStep);
          handler();
        },
      );
    };

    return <ContinueButton onClick={handlerWrapper} label={t(label)} ariaLabel={t(ariaLabel)} disabled={disabled} />;
  }

  renderSSLNotice() {
    return (
      <div className={styles.sslNotice} data-hook={'wcn-ssl-notice'} aria-hidden={'true'}>
        <LockLocked /> {t('payments.app.sslNotice')}
      </div>
    );
  }

  renderUserInfoForm() {
    const { order, userInfoFormDisabled, userInfoFormData } = this.state;

    let initialUserInfo: UserInfo = userInfoFormData || ({} as UserInfo);

    if (order.description.billingAddress) {
      const { billingAddress } = order.description;

      initialUserInfo = {
        firstName: billingAddress.firstName || initialUserInfo.firstName || '',
        lastName: billingAddress.lastName || initialUserInfo.lastName || '',
        email: billingAddress.email || initialUserInfo.email || '',
        phone: billingAddress.phone || initialUserInfo.phone || '',
        country: (billingAddress.countryCode as CountryAlpha3) || initialUserInfo.country || '',
      } as const;
    }

    return (
      <UserInfoForm
        disabled={userInfoFormDisabled}
        ref={(userInfoForm) => (this.userInfoForm = userInfoForm)}
        appearance={this.props.deviceType}
        host={this.props.host}
        locale={this.props.locale}
        visitorId={this.props.visitorId}
        snapshotId={this.props.snapshotId}
        initialUserInfo={initialUserInfo}
        getAppInstance={this.getAppInstance}
        revalidateAppInstance={this.appInstanceResolver.revalidate}
        onChange={this.onUserInfoFormChanged}
        startModuleLoad={this.props.startModuleLoad}
        endModuleLoad={this.props.endModuleLoad}
      />
    );
  }

  renderPaymentWidget() {
    const showWidget = this.state.currentStep === PaymentProcessStep.DETAILS;

    return (
      <div data-hook={DH.WidgetWrapper} className={cn(styles.widgetWrapper, !showWidget && styles.hidden)}>
        <PaymentsWidget
          host={this.props.host}
          configuration={this.configuration}
          deviceType={this.props.deviceType}
          theme={Theme.default}
          allowManualPayment={this.props.allowManualPayment}
          allowInPersonPayment={false}
          isSaveCCEnabled={true}
          termsAndConditionsLink={this.props.termsAndConditionsLink}
          useTermsAndConditions={Boolean(this.props.termsAndConditionsLink)}
          externalSubmitButton
          mandatoryFields={this.getUserInfoFields()}
          onError={this.handlePaymentError}
          onLoadingChanged={this.handleLoadingChanged}
          onCrash={this.handleWidgetCrash}
          onPaymentMethodsLoaded={this.handlePaymentMethodsLoad}
          onPaymentStart={this.handlePaymentStart}
          onPaymentComplete={this.handlePaymentComplete}
          handleErrorFetchSettings={this.handleErrorFetchSettings}
          onIsValidChanged={this.paymentWidgetIsValidChanged}
          onApiInit={this.handlePaymentsWidgetApiInit}
          walletPaymentInNewTab
          creditCardPaymentInNewTab
          isWidgetVisible={showWidget}
          revalidateAppInstance={this.appInstanceResolver.revalidate}
          onFullLoad={() => Panorama.client?.transaction(InteractionName.PAYMENTS_WIDGET_LOAD).finish()}
        />
      </div>
    );
  }

  renderPaymentComplete() {
    if (!this.props.showThankYouPage) {
      this.closeWidget();
      return null;
    }

    return (
      <PaymentComplete
        deviceType={this.props.deviceType}
        dateNumberFormat={this.props.dateNumberFormat}
        orderDescription={this.state.order.description}
        paymentResult={this.state.paymentResult}
      />
    );
  }

  renderContentAndFooter() {
    const scrollOnlyContent = this.props.deviceType === DeviceTypeEnum.desktop;
    return (
      <div className={styles.contentAndFooter} ref={!scrollOnlyContent && this.setContentScrollableRef}>
        {scrollOnlyContent ? (
          <div
            className={styles.contentScrollable}
            ref={this.setContentScrollableRef}
            onScroll={this.updateContentGradient}
            tabIndex={TabIndex.None} // FF bug - focus on scrollable content
          >
            {this.renderContent()}
          </div>
        ) : (
          this.renderContent()
        )}
        {scrollOnlyContent && this.state.contentGradient && <div className={styles.contentGradient} />}
        {this.renderFooter()}
      </div>
    );
  }

  renderContent() {
    const { currentStep, generalErrorMessage } = this.state;

    return (
      <div className={styles.content} ref={this.setContentRef}>
        {currentStep !== PaymentProcessStep.COMPLETE && this.renderStepTitle()}
        {generalErrorMessage && <GeneralError generalErrorMessage={generalErrorMessage} />}
        {currentStep === PaymentProcessStep.USER_INFO && this.renderUserInfoForm()}
        {currentStep !== null && this.renderPaymentWidget()}
        {currentStep === PaymentProcessStep.COMPLETE && this.renderPaymentComplete()}
      </div>
    );
  }

  render() {
    const isLoading = this.state.initialLoading && this.props.isLoading;

    if (this.state.paymentMethods?.length === 0) {
      return <NoPaymentMethodContainer {...this.props} />;
    }

    return (
      <div
        className={cn(
          styles.root,
          this.props.deviceType === DeviceTypeEnum.mobile ? styles.appMobile : styles.appDesktop,
        )}
        data-hook={DH.Container}
        ref={(ref) => (this.rootNode = ref)}
        dir={isRTLLocale(this.props.locale) ? 'rtl' : 'ltr'}
      >
        <button
          autoFocus
          tabIndex={-1}
          className={styles.invisible}
          ref={(ref) => (this.autoFocusNode = ref)}
          data-hook="autoFocus"
          role="dialog"
          aria-label={this.state.currentStep && t(`payments.app.accessibility.${this.state.currentStep}.step`)}
        />
        {isLoading && (
          <div data-hook={DH.AppLoader}>
            <Loader />
          </div>
        )}
        <div className={cn(styles.fullScreenContainer, isLoading && styles.hidden)}>
          {this.renderHeader()}
          {this.renderContentAndFooter()}
          {this.state.isProcessing && <Loader />}
        </div>
      </div>
    );
  }
}
